<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-center justify-space-between mb-2">
      <div class="text-subtitle-1 font-weight-bold">{{ computedTitle }}</div>

      <div class="d-flex align-center">
        <v-chip
          x-small
          class="ml-2"
          :color="totalLeft === 0 ? 'grey' : 'green'"
          text-color="white"
        >
          {{ totalLeft }} left
        </v-chip>
      </div>
    </div>

    <div
      v-if="computedSubtitle"
      class="text-body-2 grey--text text--darken-1 mb-3"
    >
      {{ computedSubtitle }}
    </div>

    <div
      v-if="!categories.length"
      class="text-body-2 grey--text text--darken-1"
    >
      No sponsorship spots found.
    </div>

    <!-- Categories -->
    <div v-for="cat in categories" :key="cat.key" class="cat-card">
      <div class="cat-head">
        <div class="cat-title">{{ cat.name }}</div>

        <div class="d-flex align-center">
          <v-chip
            x-small
            :color="cat.left === 0 ? 'grey' : 'green'"
            text-color="white"
          >
            {{ cat.left }} left
          </v-chip>

          <v-chip
            v-if="cat.left === 0"
            x-small
            class="ml-2"
            color="grey"
            text-color="white"
          >
            Completed
          </v-chip>
        </div>
      </div>

      <div v-for="item in cat.items" :key="item.key" class="item-card">
        <div class="item-top">
          <div class="item-title-wrap">
            <v-icon small class="item-icon">{{ item.icon }}</v-icon>
            <div class="item-name">{{ item.name || "Item" }}</div>
          </div>

          <div class="item-price" v-if="item.amount > 0">
            {{ money(displayMonthlyPrice(item.amount)) }}/month
          </div>
        </div>

        <div class="item-stats">
          <div class="stat">
            <span class="label">Needed</span>
            <span class="value">{{ item.capacity }}</span>
          </div>

          <div class="stat">
            <span class="label">Sponsored</span>
            <span class="value success--text">{{ item.taken }}</span>
          </div>

          <div class="stat">
            <span class="label">Left</span>
            <span class="value" :class="{ 'grey--text': item.left === 0 }">
              {{ item.left }}
            </span>
          </div>
        </div>

        <v-progress-linear
          :value="item.progress"
          height="10"
          rounded
          :color="item.left === 0 ? 'grey' : 'success'"
          background-color="grey lighten-3"
          class="mt-2"
        />

        <div class="progress-caption mt-1">{{ item.progress }}% sponsored</div>
      </div>
    </div>

    <!-- Footer -->
    <div class="d-flex align-center justify-space-between mt-3">
      <div class="text-caption grey--text text--darken-1">
        Total: <strong>{{ totalSeats }}</strong
        >, Sponsored: <strong>{{ totalTaken }}</strong
        >, Left: <strong>{{ totalLeft }}</strong>
      </div>

      <v-btn
        small
        color="success"
        rounded
        :disabled="totalLeft === 0"
        @click="$emit('donate')"
      >
        {{ totalLeft === 0 ? "Completed" : "Sponsor now" }}
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: "SponsorSeatGrid",
  props: {
    details: { type: Array, default: () => [] },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    kind: { type: String, default: "student" }, // student | unit | fast
    durationType: { type: String, default: "" }, // yearly | monthly
  },

  computed: {
    isYearlySponsor() {
      if (this.normalizedKind === "unit") return false;

      const dt = (this.durationType || "").toString().toLowerCase().trim();
      if (dt) return dt === "yearly";

      return true;
    },

    normalizedKind() {
      const k = (this.kind || "").toString().toLowerCase().trim();
      if (k === "units") return "unit";
      return k || "student";
    },

    computedTitle() {
      if (this.title) return this.title;
      if (this.normalizedKind === "unit") return "Unit sponsorship";
      if (this.normalizedKind === "fast") return "Sponsor a day";
      return "Student sponsorship";
    },

    computedSubtitle() {
      if (this.subtitle) return this.subtitle;

      if (this.normalizedKind === "unit") {
        return "Support these monthly expenses by sponsoring available units.";
      }

      if (this.normalizedKind === "fast") {
        return "Pick a day to sponsor. Completed days are marked.";
      }

      return "Pick a student to sponsor. Completed spots are marked.";
    },

    categories() {
      const rows = Array.isArray(this.details) ? this.details : [];
      const byCat = new Map();

      rows.forEach((d) => {
        if (!d) return;

        const category = (d.category || d.Category || "General")
          .toString()
          .trim();

        const name = (d.name || d.title || d.studentName || "Item")
          .toString()
          .trim();

        const amount = Number(d.amount ?? d.price ?? d.goalAmount ?? 0) || 0;

        const capRaw = d.capacity ?? d.qty ?? d.quantity ?? 1;
        const capNum =
          capRaw === null || capRaw === undefined || capRaw === ""
            ? 1
            : Number(capRaw) || 0;
        const capacity = capNum > 0 ? capNum : 1;

        const received =
          Number(d.received ?? d.collectedAmount ?? d.raised ?? 0) || 0;

        const remainingQty =
          d.remainingQty === null || d.remainingQty === undefined
            ? null
            : Number(d.remainingQty);

        const takenFromSold = Math.min(
          capacity,
          Math.max(0, Number(d.soldQty ?? d.sold ?? 0) || 0)
        );

        const takenFromRemaining =
          remainingQty !== null && !isNaN(remainingQty)
            ? Math.min(
                capacity,
                Math.max(0, capacity - Math.max(0, remainingQty))
              )
            : 0;

        const takenFromReceived =
          amount > 0 && received > 0
            ? Math.min(capacity, Math.max(0, Math.floor(received / amount)))
            : 0;

        const takenCount = Math.max(
          takenFromSold,
          takenFromRemaining,
          takenFromReceived
        );

        if (!byCat.has(category)) byCat.set(category, new Map());
        const byItem = byCat.get(category);

        if (!byItem.has(name)) {
          byItem.set(name, {
            key: `${category}-${name}`,
            name,
            amount,
            capacity: 0,
            taken: 0,
            icon: this.pickIcon(name, category),
          });
        }

        const item = byItem.get(name);

        item.capacity += capacity;
        item.taken += takenCount;

        if (!item.amount && amount) item.amount = amount;
      });

      const out = [];

      for (const [catName, itemMap] of byCat.entries()) {
        const items = Array.from(itemMap.values()).map((it) => {
          const left = Math.max(0, it.capacity - it.taken);
          const progress = it.capacity
            ? Math.min(100, Math.round((it.taken / it.capacity) * 100))
            : 0;

          return {
            ...it,
            left,
            progress,
          };
        });

        items.sort((a, b) => {
          if (a.left === 0 && b.left > 0) return 1;
          if (a.left > 0 && b.left === 0) return -1;
          return (a.name || "").localeCompare(b.name || "");
        });

        const total = items.reduce((sum, it) => sum + it.capacity, 0);
        const taken = items.reduce((sum, it) => sum + it.taken, 0);
        const left = Math.max(0, total - taken);

        out.push({
          key: `cat-${catName}`,
          name: catName,
          total,
          taken,
          left,
          items,
        });
      }

      out.sort((a, b) => {
        if (a.left === 0 && b.left > 0) return 1;
        if (a.left > 0 && b.left === 0) return -1;
        return (a.name || "").localeCompare(b.name || "");
      });

      return out;
    },

    totalSeats() {
      return this.categories.reduce((sum, c) => sum + c.total, 0);
    },

    totalTaken() {
      return this.categories.reduce((sum, c) => sum + c.taken, 0);
    },

    totalLeft() {
      return Math.max(0, this.totalSeats - this.totalTaken);
    },
  },

  methods: {
    normalizeText(s) {
      return (s || "").toString().toLowerCase().trim();
    },

    displayMonthlyPrice(amount) {
      const n = Number(amount || 0);
      if (!n) return 0;

      if (this.normalizedKind === "unit") return n;
      if (this.isYearlySponsor) return n / 12;

      return n;
    },

    pickIcon(itemName, catName) {
      if (this.normalizedKind !== "unit") return "mdi-account";

      const txt = `${this.normalizeText(catName)} ${this.normalizeText(
        itemName
      )}`;

      const rules = [
        {
          keys: ["cleaning", "janitorial", "housekeeping", "sanitation"],
          icon: "mdi-broom",
        },
        {
          keys: ["electric", "electricity", "power", "kwh"],
          icon: "mdi-flash",
        },
        { keys: ["gas", "heat", "heating"], icon: "mdi-fire" },
        { keys: ["water", "aqua", "sewer", "plumbing"], icon: "mdi-water" },
        {
          keys: ["fire alarm", "smoke", "sprinkler"],
          icon: "mdi-smoke-detector",
        },
        {
          keys: ["alarm", "security", "adt", "camera", "cctv"],
          icon: "mdi-shield-home",
        },
        {
          keys: ["security guard", "guard", "patrol"],
          icon: "mdi-account-shield",
        },
        { keys: ["internet", "wifi", "network"], icon: "mdi-wifi" },
        { keys: ["phone", "mobile", "cell"], icon: "mdi-cellphone" },
        { keys: ["rent", "lease"], icon: "mdi-home-city" },
        {
          keys: ["landscape", "landscaping", "lawn", "grass", "snow"],
          icon: "mdi-tree",
        },
        { keys: ["parking", "lot", "garage", "meter"], icon: "mdi-parking" },
        {
          keys: ["insurance", "liability", "policy"],
          icon: "mdi-shield-check",
        },
      ];

      for (const r of rules) {
        if (r.keys.some((k) => txt.includes(k))) return r.icon;
      }

      return "mdi-cube-outline";
    },

    money(val) {
      const n = Number(val || 0);
      return n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },
  },
};
</script>

<style scoped>
.cat-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 12px;
  background: #fff;
  margin-bottom: 12px;
}

.cat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cat-title {
  font-weight: 900;
  font-size: 14px;
}

.item-card {
  padding: 12px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.item-card:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.item-title-wrap {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 8px;
}

.item-icon {
  color: rgba(0, 0, 0, 0.65);
  flex: 0 0 auto;
}

.item-name {
  font-weight: 800;
  font-size: 14px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-price {
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.item-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 2px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}

.label {
  color: rgba(0, 0, 0, 0.6);
}

.value {
  font-weight: 800;
}

.progress-caption {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

@media (max-width: 600px) {
  .item-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .item-price {
    margin-left: 28px;
  }

  .item-stats {
    gap: 10px 14px;
  }
}
</style>